import { isPtacPathwaySettings } from '@alpinehomeair/common';
import { createAsyncThunk } from '@reduxjs/toolkit';
import { RootState } from 'app/store';
import {
  CondenserType,
  ConfigurationType,
  ZoneItemType,
  ZoneRefType,
  ZoneType
} from 'data/types';
import { produce } from 'immer';
import { nanoid } from 'nanoid';
import {
  computeCondenserAirHandlerAssignment,
  getAirHandlersFromConfig,
  getVisibleCondensers
} from 'utils/auto-lineset-assignment';
import { buildConcealedDuctLayoutsForConfiguration } from 'utils/concealed-duct-geometry';
import { getNextCustomConfigurationName } from 'utils/configuration-generation';
import { withConfigurationLinesets } from 'utils/configuration-linesets';
import {
  condenserHasZoneCapacity,
  condenserZoneCapacityMessage
} from 'utils/condenser-zone-capacity';
import {
  defaultConfigurationType,
  defaultZoneTypeForCondenser
} from 'utils/design-equipment-types';
import {
  buildDuctedDesignConfiguration,
  getNextDuctedDesignConfigurationName
} from 'utils/ducted-design-configuration';
import {
  getDuctedPairZone,
  selectActiveDuctedDesign,
  selectEligibleDuctedSystems
} from 'utils/ducted-design';
import { findDuctedDesignConfiguration } from 'utils/ducted-design-predicates';
import { planDuctedNewSystemSlots } from 'utils/ducted-new-systems';
import { DuctedNewSystemSlotsPlan } from 'utils/ducted-new-systems-types';
import {
  nextUnlinkedExistingSystemId,
  withDuctedPairLinks,
  withoutOrphanedDuctedPairLinks
} from 'utils/ducted-pair-links';
import { getChooseSystemsEligibleDuctedSystems } from 'utils/existing-equipment';
import {
  addExistingSystem,
  removeExistingSystem
} from 'features/structure/structure-slice';
import {
  createCondenser,
  createConfiguration,
  createZone,
  createZoneItem,
  findCondenserById,
  findConfigurationById,
  findZoneById,
  findZoneContainingSpace,
  hasNonPlaceholderConfiguration,
  markCondensersStale,
  renumberZones
} from 'utils/configurations';
import { getGridSpacesForLevel } from 'utils/grid';
import {
  buildHybridAutoConfigurations,
  isHybridAutoGeneratedConfigName
} from 'utils/hybrid-config-autobuild';
import {
  buildHybridWholeHouseConfiguration,
  findHybridWholeHouseConfiguration,
  hybridWholeHouseSignature
} from 'utils/hybrid-whole-house-configuration';
import { buildLinesetForPair } from 'utils/linesets';
import {
  autoGeneratePtacConfiguration,
  buildEmptyPtacUnit,
  newCustomPtacConfiguration,
  newPtacConfiguration,
  ptacConfigurationSignature,
  refreshPtacConfiguration
} from 'utils/ptac-configuration';
import { rederivePtacConfigurationIn } from 'utils/ptac-configuration-store';
import { nextPtacGeneratedConfigurationName } from 'utils/ptac-generation';
import { findSpaceById } from 'utils/structure';
import { structureOrientationSetting } from 'utils/structure-orientation';
import { errorModal, notification, warningModal } from 'utils/ui';
import { selectRehomeCondenserForZoneSpaces } from 'utils/zone-condenser-selection';
import {
  addLocallyCreatedConfigurationId,
  addProtectedConfigurationIdDuringGeneration,
  setActiveConfig,
  setConfigurationOptions,
  setConfigurations
} from './configurations-slice';

export const addNewConfiguration = createAsyncThunk<
  ConfigurationType | null,
  { name?: string; type?: 'ductless' | 'ducted' | 'hybrid' }
>(
  'configurations/addNewConfiguration',
  async ({ name, type }, { getState, dispatch }) => {
    const state = getState() as RootState;
    const { configurations } = state.configurations;

    const configurationName = name ?? 'New Configuration';
    const newConfig = isPtacPathwaySettings(state.project.projectSettings)
      ? newCustomPtacConfiguration(configurationName)
      : createConfiguration({
          name: configurationName,
          type: type ?? defaultConfigurationType('ductless'),
          condensers: []
        });

    const newConfigurations = produce(configurations, (draft) => {
      draft.push(newConfig);
    });

    dispatch(setConfigurations(newConfigurations));
    dispatch(addLocallyCreatedConfigurationId(newConfig.uniqueId));
    if (state.ai.configGenerating) {
      dispatch(addProtectedConfigurationIdDuringGeneration(newConfig.uniqueId));
    }
    return newConfig;
  }
);

export type CreateDeterministicHybridConfigurationArg = {
  replaceExisting?: boolean;
};

export const createDeterministicHybridConfiguration = createAsyncThunk<
  ConfigurationType[] | null,
  CreateDeterministicHybridConfigurationArg | void
>(
  'configurations/createDeterministicHybridConfiguration',
  async (arg, { getState, dispatch }) => {
    const state = getState() as RootState;
    const { configurations } = state.configurations;
    const { structure, autoLayoutSpaceIds } = state.structure;
    const replaceExisting = arg?.replaceExisting === true;

    if (!replaceExisting && hasNonPlaceholderConfiguration(configurations)) {
      return null;
    }

    const newConfigs = buildHybridAutoConfigurations(
      structure,
      autoLayoutSpaceIds
    );
    if (newConfigs.length === 0) return [];

    const kept = replaceExisting
      ? configurations.filter(
          (config) => !isHybridAutoGeneratedConfigName(config.name)
        )
      : configurations;
    const newConfigurations = [...kept, ...newConfigs];

    dispatch(setConfigurations(newConfigurations));
    newConfigs.forEach((config) => {
      dispatch(addLocallyCreatedConfigurationId(config.uniqueId));
      if (state.ai.configGenerating) {
        dispatch(addProtectedConfigurationIdDuringGeneration(config.uniqueId));
      }
    });
    return newConfigs;
  }
);

export const addNewCondenserToConfiguration = createAsyncThunk<
  CondenserType | null,
  { configurationId: string; condenserName?: string }
>(
  'configurations/addNewCondenserToConfiguration',
  async ({ configurationId, condenserName }, { getState, dispatch }) => {
    const state = getState() as RootState;
    const { configurations } = state.configurations;

    const config = findConfigurationById(configurations, configurationId);
    if (!config) {
      console.error('Configuration not found');
      errorModal('Configuration not found');
      return null;
    }

    const newCondenser = createCondenser({ name: condenserName });

    const newConfigurations = produce(configurations, (draft) => {
      const existingConfig = findConfigurationById(draft, configurationId);
      if (existingConfig) {
        existingConfig.condensers.push(newCondenser);
      }
    });

    dispatch(setConfigurations(newConfigurations));
    return newCondenser;
  }
);

export const addNewZoneToCondenser = createAsyncThunk<
  ZoneType | null,
  { configurationId: string; condenserId: string; zoneName?: string }
>(
  'configurations/addNewZoneToCondenser',
  async (
    { configurationId, condenserId, zoneName },
    { getState, dispatch }
  ) => {
    const state = getState() as RootState;
    const { configurations } = state.configurations;

    const config = findConfigurationById(configurations, configurationId);
    if (!config) {
      console.error('Configuration not found');
      errorModal(`Configuration not found`);
      return null;
    }

    const condenser = findCondenserById(config, condenserId);
    if (!condenser) {
      console.error('Condenser not found');
      errorModal('Condenser not found');
      return null;
    }

    const newZone = createZone({ name: zoneName });

    const newConfigurations = produce(configurations, (draft) => {
      const draftConfig = findConfigurationById(draft, configurationId);
      if (!draftConfig) return;
      const draftCondenser = findCondenserById(draftConfig, condenserId);
      if (draftCondenser) {
        draftCondenser.zones.push(newZone);
      }
    });

    dispatch(setConfigurations(newConfigurations));
    return newZone;
  }
);

export const addNewZoneItemToZone = createAsyncThunk<
  ZoneItemType | null,
  { configurationId: string; condenserId: string; zoneId: string }
>(
  'configurations/addNewZoneItemToZone',
  async ({ configurationId, condenserId, zoneId }, { getState, dispatch }) => {
    const state = getState() as RootState;
    const { configurations } = state.configurations;

    const config = findConfigurationById(configurations, configurationId);
    if (!config) {
      console.error('Configuration not found');
      errorModal('Configuration not found');
      return null;
    }

    const condenser = findCondenserById(config, condenserId);
    if (!condenser) {
      console.error('Condenser not found');
      errorModal('Condenser not found');
      return null;
    }

    const zone = findZoneById(condenser, zoneId);
    if (!zone) {
      console.error('Zone not found');
      errorModal('Zone not found');
      return null;
    }

    const newItem = createZoneItem();

    const newConfigurations = produce(configurations, (draft) => {
      const draftConfig = findConfigurationById(draft, configurationId);
      if (!draftConfig) return;
      const draftCondenser = findCondenserById(draftConfig, condenserId);
      if (!draftCondenser) return;
      const draftZone = findZoneById(draftCondenser, zoneId);
      if (draftZone) {
        draftZone.zoneItems.push(newItem);
      }
    });

    dispatch(setConfigurations(newConfigurations));
    return newItem;
  }
);

export const moveZoneToCondenser = createAsyncThunk<
  void,
  {
    zoneId: string;
    fromCondenserId: string;
    toCondenserId: string;
    configurationId: string;
  }
>(
  'configurations/moveZoneToCondenser',
  async (
    { zoneId, fromCondenserId, toCondenserId, configurationId },
    { getState, dispatch }
  ) => {
    const state = getState() as RootState;
    const { configurations } = state.configurations;

    const config = findConfigurationById(configurations, configurationId);
    if (!config) {
      console.error('Configuration not found');
      errorModal('Configuration not found');
      return;
    }

    if (fromCondenserId !== toCondenserId) {
      const toCondenser = findCondenserById(config, toCondenserId);
      if (toCondenser && !condenserHasZoneCapacity(toCondenser)) {
        notification(condenserZoneCapacityMessage(toCondenser), 'warning');
        return;
      }
    }

    const newConfigurations = produce(configurations, (draft) => {
      const draftConfig = findConfigurationById(draft, configurationId);
      if (!draftConfig) return;

      const fromCondenser = findCondenserById(draftConfig, fromCondenserId);
      if (!fromCondenser) return;
      const zoneIndex = fromCondenser.zones.findIndex(
        (z) => z.uniqueId === zoneId
      );
      if (zoneIndex === -1) return;
      const [zoneToMove] = fromCondenser.zones.splice(zoneIndex, 1);

      const draftToCondenser = findCondenserById(draftConfig, toCondenserId);
      if (!draftToCondenser) return;
      draftToCondenser.zones.push(zoneToMove);
    });

    dispatch(setConfigurations(newConfigurations));
  }
);

export const moveZoneItemToZone = createAsyncThunk<
  void,
  {
    zoneItemId: string;
    fromZoneId: string;
    fromCondenserId: string;
    toZoneId: string;
    toCondenserId: string;
    configurationId: string;
  }
>(
  'configurations/moveZoneItemToZone',
  async (
    {
      zoneItemId,
      fromZoneId,
      fromCondenserId,
      toZoneId,
      toCondenserId,
      configurationId
    },
    { getState, dispatch }
  ) => {
    const state = getState() as RootState;
    const { configurations } = state.configurations;

    const config = findConfigurationById(configurations, configurationId);
    if (!config) {
      console.error('Configuration not found');
      errorModal('Configuration not found');
      return;
    }

    const newConfigurations = produce(configurations, (draft) => {
      const draftConfig = findConfigurationById(draft, configurationId);
      if (!draftConfig) return;

      const fromCondenser = findCondenserById(draftConfig, fromCondenserId);
      if (!fromCondenser) return;
      const zoneFrom = findZoneById(fromCondenser, fromZoneId);
      if (!zoneFrom) return;

      const itemIndex = zoneFrom.zoneItems.findIndex(
        (i) => i.uniqueId === zoneItemId
      );
      if (itemIndex === -1) return;
      const [zoneItem] = zoneFrom.zoneItems.splice(itemIndex, 1);

      const toCondenser = findCondenserById(draftConfig, toCondenserId);
      if (!toCondenser) return;
      const zoneTo = findZoneById(toCondenser, toZoneId);
      if (!zoneTo) return;
      zoneTo.zoneItems.push(zoneItem);
    });

    dispatch(
      setConfigurations(
        isPtacPathwaySettings(state.project.projectSettings)
          ? rederivePtacConfigurationIn(
              newConfigurations,
              config,
              state.structure.structure
            )
          : newConfigurations
      )
    );
  }
);

export const moveCondenserInConfiguration = createAsyncThunk<
  void,
  { fromCondenserId: string; toCondenserId: string; configurationId: string }
>(
  'configurations/moveCondenserInConfiguration',
  async (
    { fromCondenserId, toCondenserId, configurationId },
    { getState, dispatch }
  ) => {
    const state = getState() as RootState;
    const { configurations } = state.configurations;

    const config = findConfigurationById(configurations, configurationId);
    if (!config) {
      console.error('Configuration not found');
      errorModal('Configuration not found');
      return;
    }

    const newConfigurations = produce(configurations, (draft) => {
      const draftConfig = findConfigurationById(draft, configurationId);
      if (!draftConfig) return;

      const fromIndex = draftConfig.condensers.findIndex(
        (c) => c.uniqueId === fromCondenserId
      );
      const toIndex = draftConfig.condensers.findIndex(
        (c) => c.uniqueId === toCondenserId
      );

      if (fromIndex === -1 || toIndex === -1) return;

      const [condenserToMove] = draftConfig.condensers.splice(fromIndex, 1);
      // Insert condenser at the "toIndex"
      draftConfig.condensers.splice(toIndex, 0, condenserToMove);
    });

    dispatch(setConfigurations(newConfigurations));
  }
);

export const moveZoneItemToNewZone = createAsyncThunk<
  void,
  {
    configurationId: string;
    fromCondenserId: string;
    zoneItemId: string;
    toCondenserId: string;
    newZoneType: 'ductless' | 'ducted';
  }
>(
  'configurations/moveZoneItemToNewZone',
  async (
    {
      configurationId,
      fromCondenserId,
      zoneItemId,
      toCondenserId,
      newZoneType
    },
    { getState, dispatch }
  ) => {
    const state = getState() as RootState;
    const { configurations } = state.configurations;

    const config = findConfigurationById(configurations, configurationId);
    if (!config) {
      console.error('Configuration not found');
      errorModal('Configuration not found');
      return;
    }

    const toCondenser = findCondenserById(config, toCondenserId);
    if (toCondenser && !condenserHasZoneCapacity(toCondenser)) {
      notification(condenserZoneCapacityMessage(toCondenser), 'warning');
      return;
    }

    const newConfigurations = produce(configurations, (draft) => {
      const draftConfig = findConfigurationById(draft, configurationId);
      if (!draftConfig) return;

      const fromCondenser = findCondenserById(draftConfig, fromCondenserId);
      const draftToCondenser = findCondenserById(draftConfig, toCondenserId);
      if (!fromCondenser || !draftToCondenser) return;

      // find the zone/item and remove it from fromCondenser
      let itemToMove: ZoneItemType | null = null;
      fromCondenser.zones.forEach((z) => {
        const idx = z.zoneItems.findIndex((i) => i.uniqueId === zoneItemId);
        if (idx !== -1) {
          [itemToMove] = z.zoneItems.splice(idx, 1);
        }
      });
      if (!itemToMove) return;

      // find a new zone name that isn't used yet
      let zoneNumber = 1;
      const zoneNames = draftConfig.condensers.flatMap((cond) =>
        cond.zones.map((zone) => zone.name)
      );
      const baseLabel = newZoneType === 'ductless' ? 'Zone ' : 'Ducted Zone ';
      while (zoneNames.includes(`${baseLabel}${zoneNumber}`)) {
        zoneNumber += 1;
      }

      // create a brand new zone
      draftToCondenser.zones.push({
        uniqueId: nanoid(5),
        name: `${baseLabel}${zoneNumber}`,
        type: newZoneType,
        zoneItems: [itemToMove],
        modified: true
      });

      draftToCondenser.modified = true;
    });

    dispatch(setConfigurations(newConfigurations));
  }
);

export const addZoneWithItemToCondenser = createAsyncThunk<
  void,
  {
    configurationId: string;
    condenserId: string;
    zoneType: 'ductless' | 'ducted';
    structureItem?: {
      structureItemId: string;
      label: string;
      BTUs: ZoneItemType['BTUs'];
    };
    baseLabel?: string; // e.g. "Zone" or "Mini-Split Zone"
  }
>(
  'configurations/addZoneWithItemToCondenser',
  async (
    { configurationId, condenserId, zoneType, structureItem, baseLabel },
    { getState, dispatch }
  ) => {
    const state = getState() as RootState;
    const { configurations } = state.configurations;

    const config = findConfigurationById(configurations, configurationId);
    if (!config) {
      console.error('Configuration not found');
      errorModal('Configuration not found');
      return;
    }

    const targetCondenser = findCondenserById(config, condenserId);
    if (!targetCondenser) {
      console.error('Condenser not found');
      errorModal('Condenser not found');
      return;
    }
    if (!condenserHasZoneCapacity(targetCondenser)) {
      warningModal(condenserZoneCapacityMessage(targetCondenser));
      return;
    }

    const newConfigurations = produce(configurations, (draft) => {
      const draftConfig = findConfigurationById(draft, configurationId);
      if (!draftConfig) return;
      const draftCondenser = findCondenserById(draftConfig, condenserId);
      if (!draftCondenser) return;

      let zoneNumber = 1;
      const label = baseLabel || 'Zone';
      const zoneNames = draftConfig.condensers.flatMap((cond) =>
        cond.zones.map((zone) => zone.name)
      );
      while (zoneNames.includes(`${label} ${zoneNumber}`)) {
        zoneNumber += 1;
      }

      // create new zone
      const newZoneId = nanoid(5);
      const newZone: ZoneType = {
        uniqueId: newZoneId,
        name: `${label} ${zoneNumber}`,
        type: zoneType,
        zoneItems: [],
        modified: false
      };

      // if we have a structure item, add it to the zone
      if (structureItem) {
        newZone.zoneItems.push({
          uniqueId: nanoid(5),
          structureItemId: structureItem.structureItemId,
          name: structureItem.label,
          BTUs: structureItem.BTUs,
          originalBTUs: structureItem.BTUs,
          isPartition: false
        });
        newZone.modified = true;
      }
      draftCondenser.zones.push(newZone);

      // mark condenser as modified if a structureItem was added
      if (structureItem) {
        draftCondenser.modified = true;
      }
    });

    dispatch(setConfigurations(newConfigurations));
  }
);

/**
 * Move one zone above/below another zone by splicing at the index of the target zone.
 */
export const moveZoneInCondenser = createAsyncThunk<
  void,
  {
    configurationId: string;
    fromZoneId: string;
    toZoneId: string; // the "destination" zone
  }
>(
  'configurations/moveZoneInCondenser',
  async ({ configurationId, fromZoneId, toZoneId }, { getState, dispatch }) => {
    const state = getState() as RootState;
    const { configurations } = state.configurations;

    const newConfigs = produce(configurations, (draft) => {
      const draftConfig = findConfigurationById(draft, configurationId);
      if (!draftConfig) return;

      // Find the "from" condenser & zone
      let fromCondenserIdx = -1;
      let zoneToMoveIdx = -1;
      let zoneToMove: ZoneType | null = null;

      draftConfig.condensers.forEach((cond, cIdx) => {
        const zIdx = cond.zones.findIndex((z) => z.uniqueId === fromZoneId);
        if (zIdx !== -1) {
          fromCondenserIdx = cIdx;
          zoneToMoveIdx = zIdx;
          zoneToMove = cond.zones[zIdx];
        }
      });

      if (!zoneToMove || fromCondenserIdx === -1) return;

      // Remove from original location
      draftConfig.condensers[fromCondenserIdx].zones.splice(zoneToMoveIdx, 1);

      // Find the "to" zone so we can splice
      draftConfig.condensers.forEach((cond) => {
        const targetIndex = cond.zones.findIndex(
          (z) => z.uniqueId === toZoneId
        );
        if (targetIndex !== -1 && zoneToMove) {
          cond.zones.splice(targetIndex, 0, zoneToMove);
        }
      });
    });

    dispatch(setConfigurations(newConfigs));
  }
);

export const moveConfigurationItem = createAsyncThunk<
  void,
  { fromUniqueId: string; toUniqueId: string }
>(
  'configurations/moveConfigurationItem',
  async ({ fromUniqueId, toUniqueId }, { getState, dispatch }) => {
    const state = getState() as RootState;
    const { configurations } = state.configurations;

    type FoundInfo =
      | {
          itemType: 'condenser';
          configIndex: number;
          condenserIndex: number;
        }
      | {
          itemType: 'zone';
          configIndex: number;
          condenserIndex: number;
          zoneIndex: number;
        }
      | {
          itemType: 'zoneItem';
          configIndex: number;
          condenserIndex: number;
          zoneIndex: number;
          zoneItemIndex: number;
        }
      | null;

    const locateItem = (id: string): FoundInfo => {
      for (let c = 0; c < configurations.length; c += 1) {
        const conf = configurations[c];
        const condenserIndex = conf.condensers.findIndex(
          (d) => d.uniqueId === id
        );
        if (condenserIndex !== -1) {
          return {
            itemType: 'condenser',
            configIndex: c,
            condenserIndex
          };
        }
        for (let k = 0; k < conf.condensers.length; k += 1) {
          const zIndex = conf.condensers[k].zones.findIndex(
            (z) => z.uniqueId === id
          );
          if (zIndex !== -1) {
            return {
              itemType: 'zone',
              configIndex: c,
              condenserIndex: k,
              zoneIndex: zIndex
            };
          }
          const zoneArr = conf.condensers[k].zones;
          for (let z = 0; z < zoneArr.length; z += 1) {
            const ziIndex = zoneArr[z].zoneItems.findIndex(
              (zi) => zi.uniqueId === id
            );
            if (ziIndex !== -1) {
              return {
                itemType: 'zoneItem',
                configIndex: c,
                condenserIndex: k,
                zoneIndex: z,
                zoneItemIndex: ziIndex
              };
            }
          }
        }
      }
      return null;
    };

    const fromInfo = locateItem(fromUniqueId);
    const toInfo = locateItem(toUniqueId);

    if (!fromInfo) {
      return;
    }
    if (!toInfo) {
      return;
    }

    const getHierarchyLevel = (info: FoundInfo) => {
      if (!info) return -1;
      switch (info.itemType) {
        case 'condenser':
          return 1;
        case 'zone':
          return 2;
        case 'zoneItem':
          return 3;
        default:
          return -1;
      }
    };

    const fromLevel = getHierarchyLevel(fromInfo);
    const toLevel = getHierarchyLevel(toInfo);

    if (fromLevel < toLevel) {
      return;
    }

    if (
      toInfo.itemType === 'condenser' &&
      (fromInfo.itemType === 'zone' || fromInfo.itemType === 'zoneItem')
    ) {
      const toCondenser =
        configurations[toInfo.configIndex].condensers[toInfo.condenserIndex];
      const movingZoneWithinSameCondenser =
        fromInfo.itemType === 'zone' &&
        fromInfo.configIndex === toInfo.configIndex &&
        fromInfo.condenserIndex === toInfo.condenserIndex;
      if (
        !movingZoneWithinSameCondenser &&
        !condenserHasZoneCapacity(toCondenser)
      ) {
        notification(condenserZoneCapacityMessage(toCondenser), 'warning');
        return;
      }
    }

    const newConfigurations = produce(configurations, (draft) => {
      let itemToMove: CondenserType | ZoneType | ZoneItemType | null = null;

      function removeFromOldLocation() {
        if (!fromInfo) return;
        const draftConfig = draft[fromInfo.configIndex];
        if (fromInfo.itemType === 'condenser') {
          const removed = draftConfig.condensers.splice(
            fromInfo.condenserIndex,
            1
          );
          [itemToMove] = removed;
        } else if (fromInfo.itemType === 'zone') {
          const cond = draftConfig.condensers[fromInfo.condenserIndex];
          const removed = cond.zones.splice(fromInfo.zoneIndex, 1);
          [itemToMove] = removed;
        } else {
          const cond = draftConfig.condensers[fromInfo.condenserIndex];
          const zn = cond.zones[fromInfo.zoneIndex];
          const removed = zn.zoneItems.splice(fromInfo.zoneItemIndex, 1);
          [itemToMove] = removed;
        }
      }

      function reorderSameType() {
        if (!itemToMove || !toInfo) return;
        const draftConfig = draft[toInfo.configIndex];
        if (
          fromInfo?.itemType === 'condenser' &&
          toInfo.itemType === 'condenser'
        ) {
          draftConfig.condensers.splice(
            toInfo.condenserIndex,
            0,
            itemToMove as CondenserType
          );
        } else if (
          fromInfo?.itemType === 'zone' &&
          toInfo.itemType === 'zone'
        ) {
          const cond = draftConfig.condensers[toInfo.condenserIndex];
          cond.zones.splice(toInfo.zoneIndex, 0, itemToMove as ZoneType);
        } else if (
          fromInfo?.itemType === 'zoneItem' &&
          toInfo.itemType === 'zoneItem'
        ) {
          const cond = draftConfig.condensers[toInfo.condenserIndex];
          const zn = cond.zones[toInfo.zoneIndex];
          zn.zoneItems.splice(
            toInfo.zoneItemIndex,
            0,
            itemToMove as ZoneItemType
          );
        }
      }

      function moveZoneToThisCondenser() {
        if (!itemToMove || !toInfo || toInfo.itemType !== 'condenser') return;
        const draftConfig = draft[toInfo.configIndex];
        const cond = draftConfig.condensers[toInfo.condenserIndex];
        cond.zones.push(itemToMove as ZoneType);
      }

      function moveZoneItemToThisZone() {
        if (!itemToMove || !toInfo || toInfo.itemType !== 'zone') return;
        const draftConfig = draft[toInfo.configIndex];
        const cond = draftConfig.condensers[toInfo.condenserIndex];
        const zn = cond.zones[toInfo.zoneIndex];
        zn.zoneItems.push(itemToMove as ZoneItemType);
      }

      function moveZoneItemToThisCondenser() {
        if (!itemToMove || !toInfo || toInfo.itemType !== 'condenser') return;
        const draftConfig = draft[toInfo.configIndex];
        const cond = draftConfig.condensers[toInfo.condenserIndex];
        let zoneNumber = 1;
        const existingZones = cond.zones.map((z) => z.name);
        while (existingZones.includes(`New Zone ${zoneNumber}`)) {
          zoneNumber += 1;
        }
        const newZone: ZoneType = {
          uniqueId: nanoid(5),
          name: `New Zone ${zoneNumber}`,
          type: 'ductless',
          zoneItems: [itemToMove as ZoneItemType],
          modified: true
        };
        cond.zones.push(newZone);
        cond.modified = true;
      }

      removeFromOldLocation();
      if (!itemToMove) {
        return;
      }
      if (fromInfo.itemType === toInfo.itemType) {
        reorderSameType();
      } else if (
        fromInfo.itemType === 'zone' &&
        toInfo.itemType === 'condenser'
      ) {
        moveZoneToThisCondenser();
      } else if (
        fromInfo.itemType === 'zoneItem' &&
        toInfo.itemType === 'zone'
      ) {
        moveZoneItemToThisZone();
      } else if (
        fromInfo.itemType === 'zoneItem' &&
        toInfo.itemType === 'condenser'
      ) {
        moveZoneItemToThisCondenser();
      }
    });

    dispatch(setConfigurations(newConfigurations));
  }
);

export const addZoneToCondenser = createAsyncThunk<
  void,
  {
    configurationId: string;
    condenserId: string;
    zoneType: 'ductless' | 'ducted';
    unzonedItemId?: string;
  }
>(
  'configurations/addZoneToCondenser',
  async (
    { configurationId, condenserId, zoneType, unzonedItemId },
    { getState, dispatch }
  ) => {
    const state = getState() as RootState;
    const { configurationOptions } = state.configurations;
    const { structure } = state.structure;

    const config = findConfigurationById(configurationOptions, configurationId);
    if (!config) {
      console.error('Configuration not found');
      errorModal('Configuration not found');
      return;
    }

    const condenser = findCondenserById(config, condenserId);
    if (!condenser) {
      console.error('Condenser not found');
      errorModal('Condenser not found');
      return;
    }

    if (!condenserHasZoneCapacity(condenser)) {
      warningModal(condenserZoneCapacityMessage(condenser));
      return;
    }

    const newConfigurations = produce(configurationOptions, (draft) => {
      const configIndex = draft.findIndex(
        (conf) => conf.uniqueId === configurationId
      );
      if (configIndex !== -1) {
        const condenserIndex = draft[configIndex].condensers.findIndex(
          (conf) => conf.uniqueId === condenserId
        );
        if (condenserIndex !== -1) {
          let zoneNumber = 1;
          const zoneLabel =
            condenser.type !== 'ductless' ? 'Mini-Split Zone' : 'Zone';
          const zoneNames = draft[configIndex].condensers.flatMap((cond) =>
            cond.zones.map((zone) => zone.name)
          );
          while (zoneNames.includes(`${zoneLabel} ${zoneNumber}`)) {
            zoneNumber += 1;
          }
          const newZone: ZoneType = {
            uniqueId: nanoid(5),
            name: `${zoneLabel} ${zoneNumber}`,
            type: zoneType,
            zoneItems: [],
            modified: false
          };

          if (unzonedItemId) {
            const structureItem = findSpaceById(structure, unzonedItemId);
            if (structureItem) {
              const newZoneItem: ZoneItemType = {
                uniqueId: nanoid(5),
                structureItemId: unzonedItemId,
                name: structureItem.label,
                BTUs: structureItem.BTUs,
                originalBTUs: structureItem.BTUs,
                isPartition: false
              };
              newZone.zoneItems.push(newZoneItem);
            } else {
              console.error('Item not found on structure.');
              errorModal('Item not found on structure.');
            }
          }
          draft[configIndex].condensers[condenserIndex].zones.push(newZone);
          // eslint-disable-next-line no-param-reassign
          draft[configIndex].condensers[condenserIndex].modified =
            unzonedItemId !== undefined;
        }
      }
    });

    dispatch(setConfigurationOptions(newConfigurations));
  }
);

export const addUnzonedItemToConfiguration = createAsyncThunk<
  void,
  {
    configurationId: string;
    structureItem: {
      structureItemId: string;
      label: string;
      BTUs: ZoneItemType['BTUs'];
    };
    condenserId?: string;
  }
>(
  'configurations/addUnzonedItemToConfiguration',
  async (
    { configurationId, structureItem, condenserId },
    { getState, dispatch }
  ) => {
    const state = getState() as RootState;
    const { configurationOptions } = state.configurations;
    const config = findConfigurationById(configurationOptions, configurationId);

    if (!config) {
      console.error('Configuration not found');
      errorModal('Configuration not found');
      return;
    }

    if (condenserId) {
      const targetCondenser = findCondenserById(config, condenserId);
      if (targetCondenser && !condenserHasZoneCapacity(targetCondenser)) {
        notification(condenserZoneCapacityMessage(targetCondenser), 'warning');
        return;
      }
    }

    const newConfigurationOptions = produce(configurationOptions, (draft) => {
      const draftConfig = findConfigurationById(draft, configurationId);

      if (!draftConfig) {
        return;
      }

      const newZone = createZone({
        name: `Zone 1`,
        type: 'ductless',
        zoneItems: [
          {
            uniqueId: nanoid(5),
            structureItemId: structureItem.structureItemId,
            name: structureItem.label,
            BTUs: structureItem.BTUs,
            originalBTUs: structureItem.BTUs,
            isPartition: false
          }
        ]
      });

      if (condenserId) {
        const condenser = findCondenserById(draftConfig, condenserId);
        if (condenser) {
          const condenserIndex = draftConfig.condensers.findIndex(
            (cond) => cond.uniqueId === condenserId
          );

          if (condenserIndex !== -1) {
            draftConfig.condensers[condenserIndex].zones.push(newZone);
          }
        }
      } else {
        const visibleCondensersCount = draftConfig.condensers.length;
        const newCondenser = createCondenser({
          name: `Condenser ${visibleCondensersCount + 1}`,
          zones: [newZone]
        });

        draftConfig.condensers.push(newCondenser);
      }
    });

    dispatch(setConfigurationOptions(newConfigurationOptions));
  }
);

export const addCondenserToConfiguration = createAsyncThunk<
  void,
  { configurationId: string }
>(
  'configurations/addCondenserToConfiguration',
  async ({ configurationId }, { getState, dispatch }) => {
    const state = getState() as RootState;
    const { configurationOptions } = state.configurations;
    const config = findConfigurationById(configurationOptions, configurationId);

    if (!config) {
      console.error('Configuration not found');
      errorModal('Configuration not found');
      return;
    }

    const newConfigurations = produce(configurationOptions, (draft) => {
      const configIndex = draft.findIndex(
        (conf) => conf.uniqueId === configurationId
      );

      if (configIndex !== -1) {
        let condenserNumber = 1;
        let zoneNumber = 1;
        const visibleCondensers = draft[configIndex].condensers;
        const condenserNames = visibleCondensers.map((cond) => cond.name);
        const zoneNames = draft[configIndex].condensers.flatMap((cond) =>
          cond.zones.map((zone) => zone.name)
        );

        while (condenserNames.includes(`Condenser ${condenserNumber}`)) {
          condenserNumber += 1;
        }

        while (zoneNames.includes(`Zone ${zoneNumber}`)) {
          zoneNumber += 1;
        }

        const newZone = createZone({
          name: `Zone ${zoneNumber}`
        });

        const newCondenser = createCondenser({
          name: `Condenser ${condenserNumber}`,
          zones: [newZone]
        });

        draft[configIndex].condensers.push(newCondenser);
      }
    });

    dispatch(setConfigurationOptions(newConfigurations));
  }
);

export const mergeSpaceIntoZone = createAsyncThunk<
  void,
  { spaceId: string; targetSpaceId: string; configurationId: string }
>(
  'configurations/mergeSpaceIntoZone',
  async (
    { spaceId, targetSpaceId, configurationId },
    { getState, dispatch }
  ) => {
    const state = getState() as RootState;
    const { configurations } = state.configurations;

    const config = findConfigurationById(configurations, configurationId);
    if (!config) {
      errorModal('Configuration not found');
      return;
    }

    const sourceResult = findZoneContainingSpace(config, spaceId);
    const targetResult = findZoneContainingSpace(config, targetSpaceId);

    if (!sourceResult || !targetResult) {
      errorModal('Could not find zones for the specified spaces');
      return;
    }

    if (sourceResult.zone.uniqueId === targetResult.zone.uniqueId) {
      return;
    }

    const affectedCondenserIds = [
      sourceResult.condenser.uniqueId,
      targetResult.condenser.uniqueId
    ];

    const newConfigurations = produce(configurations, (draft) => {
      const draftConfig = findConfigurationById(draft, configurationId);
      if (!draftConfig) return;

      let sourceZoneItem: ZoneItemType | null = null;

      for (const condenser of draftConfig.condensers) {
        for (const zone of condenser.zones) {
          const itemIndex = zone.zoneItems.findIndex(
            (zi) => zi.structureItemId === spaceId
          );
          if (itemIndex !== -1) {
            [sourceZoneItem] = zone.zoneItems.splice(itemIndex, 1);
            break;
          }
        }
        if (sourceZoneItem) break;
      }

      if (!sourceZoneItem) return;

      for (const condenser of draftConfig.condensers) {
        const targetZone = condenser.zones.find(
          (z) => z.uniqueId === targetResult.zone.uniqueId
        );
        if (targetZone) {
          targetZone.zoneItems.push(sourceZoneItem);
          break;
        }
      }

      draftConfig.condensers.forEach((condenser) => {
        // eslint-disable-next-line no-param-reassign
        condenser.zones = condenser.zones.filter((z) => z.zoneItems.length > 0);
      });
    });

    const finalConfigurations = newConfigurations.map((c) => {
      if (c.uniqueId === configurationId) {
        return renumberZones(markCondensersStale(c, affectedCondenserIds));
      }
      return c;
    });

    dispatch(setConfigurations(finalConfigurations));
  }
);

export const separateSpaceToNewZone = createAsyncThunk<
  void,
  { spaceId: string; configurationId: string }
>(
  'configurations/separateSpaceToNewZone',
  async ({ spaceId, configurationId }, { getState, dispatch }) => {
    const state = getState() as RootState;
    const { configurations } = state.configurations;

    const config = findConfigurationById(configurations, configurationId);
    if (!config) {
      errorModal('Configuration not found');
      return;
    }

    const sourceResult = findZoneContainingSpace(config, spaceId);
    if (!sourceResult) {
      errorModal('Could not find zone for the specified space');
      return;
    }

    if (sourceResult.zone.zoneItems.length <= 1) {
      return;
    }

    if (!condenserHasZoneCapacity(sourceResult.condenser)) {
      notification(
        condenserZoneCapacityMessage(sourceResult.condenser),
        'warning'
      );
      return;
    }

    const affectedCondenserIds = [sourceResult.condenser.uniqueId];

    const newConfigurations = produce(configurations, (draft) => {
      const draftConfig = findConfigurationById(draft, configurationId);
      if (!draftConfig) return;

      let sourceZoneItem: ZoneItemType | null = null;
      let sourceCondenserIndex = -1;

      for (let cIdx = 0; cIdx < draftConfig.condensers.length; cIdx += 1) {
        const condenser = draftConfig.condensers[cIdx];
        for (const zone of condenser.zones) {
          const itemIndex = zone.zoneItems.findIndex(
            (zi) => zi.structureItemId === spaceId
          );
          if (itemIndex !== -1) {
            [sourceZoneItem] = zone.zoneItems.splice(itemIndex, 1);
            sourceCondenserIndex = cIdx;
            break;
          }
        }
        if (sourceZoneItem) break;
      }

      if (!sourceZoneItem || sourceCondenserIndex === -1) return;

      const newZone: ZoneType = {
        uniqueId: nanoid(5),
        name: 'New Zone',
        type: defaultZoneTypeForCondenser(
          draftConfig.condensers[sourceCondenserIndex]
        ),
        zoneItems: [sourceZoneItem]
      };

      draftConfig.condensers[sourceCondenserIndex].zones.push(newZone);
    });

    const finalConfigurations = newConfigurations.map((c) => {
      if (c.uniqueId === configurationId) {
        return renumberZones(markCondensersStale(c, affectedCondenserIds));
      }
      return c;
    });

    dispatch(setConfigurations(finalConfigurations));
  }
);

export const addSpacesToZone = createAsyncThunk<
  void,
  {
    configurationId: string;
    condenserId: string;
    zoneId: string;
    spaces: Array<{
      structureItemId: string;
      name: string;
      BTUs: { heating: number; cooling: number };
    }>;
  }
>(
  'configurations/addSpacesToZone',
  async (
    { configurationId, condenserId, zoneId, spaces },
    { getState, dispatch }
  ) => {
    const state = getState() as RootState;
    const { configurations } = state.configurations;

    const config = findConfigurationById(configurations, configurationId);
    if (!config) {
      console.error('Configuration not found');
      return;
    }

    if (spaces.length === 0) return;

    const condenser = findCondenserById(config, condenserId);
    if (!condenser) return;

    const targetZone = findZoneById(condenser, zoneId);
    if (!targetZone) return;

    const spaceIds = new Set(spaces.map((s) => s.structureItemId));
    const allSpacesInTargetZone = spaces.every((s) =>
      targetZone.zoneItems.some(
        (item) => item.structureItemId === s.structureItemId
      )
    );

    const { structure } = state.structure;
    const isPtac = isPtacPathwaySettings(state.project.projectSettings);
    const rehomeCondenser =
      targetZone.zoneItems.length === 0 && !isPtac
        ? selectRehomeCondenserForZoneSpaces(
            structure,
            config.condensers,
            condenserId,
            spaces.map((s) => s.structureItemId),
            findSpaceById(structure, spaces[0].structureItemId)?.position ??
              null
          )
        : null;

    const affectedCondenserIds = new Set<string>([condenserId]);
    if (rehomeCondenser) {
      affectedCondenserIds.add(rehomeCondenser.uniqueId);
    }
    config.condensers.forEach((cond) => {
      const holdsAnySpace = cond.zones.some((zone) =>
        zone.zoneItems.some((item) => spaceIds.has(item.structureItemId))
      );
      if (holdsAnySpace) {
        affectedCondenserIds.add(cond.uniqueId);
      }
    });

    const newConfigurations = produce(configurations, (draft) => {
      const draftConfig = findConfigurationById(draft, configurationId);
      if (!draftConfig) return;

      const draftCondenser = findCondenserById(draftConfig, condenserId);
      if (!draftCondenser) return;

      const draftZone = findZoneById(draftCondenser, zoneId);
      if (!draftZone) return;

      if (allSpacesInTargetZone) {
        draftZone.zoneItems = draftZone.zoneItems.filter(
          (item) => !spaceIds.has(item.structureItemId)
        );
      } else {
        draftConfig.condensers.forEach((cond) => {
          cond.zones.forEach((zone) => {
            // eslint-disable-next-line no-param-reassign
            zone.zoneItems = zone.zoneItems.filter(
              (item) => !spaceIds.has(item.structureItemId)
            );
          });
        });

        for (const space of spaces) {
          draftZone.zoneItems.push({
            uniqueId: nanoid(5),
            structureItemId: space.structureItemId,
            name: space.name,
            BTUs: space.BTUs,
            originalBTUs: space.BTUs
          });
        }
      }

      draftZone.modified = true;
      draftCondenser.modified = true;

      if (rehomeCondenser) {
        const draftRehomeCondenser = findCondenserById(
          draftConfig,
          rehomeCondenser.uniqueId
        );
        if (draftRehomeCondenser) {
          const zoneIndex = draftCondenser.zones.findIndex(
            (z) => z.uniqueId === zoneId
          );
          if (zoneIndex !== -1) {
            const [zoneToMove] = draftCondenser.zones.splice(zoneIndex, 1);
            draftRehomeCondenser.zones.push(zoneToMove);
            draftRehomeCondenser.modified = true;
          }
        }
      }
    });

    if (isPtac) {
      dispatch(
        setConfigurations(
          rederivePtacConfigurationIn(newConfigurations, config, structure)
        )
      );
      return;
    }

    const finalConfigurations = newConfigurations.map((c) => {
      if (c.uniqueId === configurationId) {
        return markCondensersStale(c, [...affectedCondenserIds]);
      }
      return c;
    });

    dispatch(setConfigurations(finalConfigurations));
  }
);

export const createPtacZone = createAsyncThunk<
  ZoneRefType | null,
  { configurationId: string }
>(
  'configurations/createPtacZone',
  async ({ configurationId }, { getState, dispatch }) => {
    const state = getState() as RootState;
    const { configurations } = state.configurations;

    const config = findConfigurationById(configurations, configurationId);
    if (!config) {
      console.error('Configuration not found');
      return null;
    }

    const totalZones = config.condensers.reduce(
      (sum, condenser) => sum + condenser.zones.length,
      0
    );
    const unit = buildEmptyPtacUnit(`Zone ${totalZones + 1}`);

    dispatch(
      setConfigurations(
        configurations.map((configuration) =>
          configuration.uniqueId === configurationId
            ? {
                ...configuration,
                condensers: [...configuration.condensers, unit]
              }
            : configuration
        )
      )
    );
    return { zoneId: unit.zones[0].uniqueId, condenserId: unit.uniqueId };
  }
);

export const createZoneOnCondenser = createAsyncThunk<
  string | null,
  {
    configurationId: string;
    condenserId: string;
  }
>(
  'configurations/createZoneOnCondenser',
  async ({ configurationId, condenserId }, { getState, dispatch }) => {
    const state = getState() as RootState;
    const { configurations } = state.configurations;

    const config = findConfigurationById(configurations, configurationId);
    if (!config) {
      console.error('Configuration not found');
      return null;
    }

    const condenser = findCondenserById(config, condenserId);
    if (!condenser) {
      console.error('Condenser not found');
      return null;
    }

    if (!condenserHasZoneCapacity(condenser)) {
      return null;
    }

    const totalZones = config.condensers.reduce(
      (sum, c) => sum + c.zones.length,
      0
    );

    const existingSystemId =
      condenser.type === 'ducted'
        ? nextUnlinkedExistingSystemId(
            config,
            selectEligibleDuctedSystems(state)
          )
        : undefined;

    const newZone = createZone({
      name: `Zone ${totalZones + 1}`,
      type: defaultZoneTypeForCondenser(condenser),
      ...(existingSystemId ? { existingSystemId } : {})
    });

    const newConfigurations = produce(configurations, (draft) => {
      const draftConfig = findConfigurationById(draft, configurationId);
      if (!draftConfig) return;

      const draftCondenser = findCondenserById(draftConfig, condenserId);
      if (!draftCondenser) return;

      draftCondenser.zones.push(newZone);
    });

    dispatch(setConfigurations(newConfigurations));
    return newZone.uniqueId;
  }
);

export const setDuctedPairLink = createAsyncThunk<
  void,
  {
    configurationId: string;
    condenserId: string;
    existingSystemId: string | null;
  }
>(
  'configurations/setDuctedPairLink',
  async (
    { configurationId, condenserId, existingSystemId },
    { getState, dispatch }
  ) => {
    const state = getState() as RootState;
    const { configurations } = state.configurations;

    const newConfigurations = produce(configurations, (draft) => {
      const draftConfig = findConfigurationById(draft, configurationId);
      if (!draftConfig) return;

      const draftCondenser = findCondenserById(draftConfig, condenserId);
      if (!draftCondenser) return;

      const zone = getDuctedPairZone(draftCondenser);
      if (!zone) return;

      if (existingSystemId) {
        zone.existingSystemId = existingSystemId;
      } else {
        delete zone.existingSystemId;
      }
      zone.modified = true;
      draftConfig.modified = true;
    });

    dispatch(setConfigurations(newConfigurations));
  }
);

export const pruneOrphanedDuctedPairLinks = createAsyncThunk<void, void>(
  'configurations/pruneOrphanedDuctedPairLinks',
  async (_, { getState, dispatch }) => {
    const state = getState() as RootState;
    const { configurations } = state.configurations;
    const existingSystemIds = new Set(
      state.structure.existingSystems.map((system) => system.uniqueId)
    );

    const newConfigurations = withoutOrphanedDuctedPairLinks(
      configurations,
      existingSystemIds
    );
    if (newConfigurations !== configurations) {
      dispatch(setConfigurations(newConfigurations));
    }
  }
);

export const removeSpaceFromZone = createAsyncThunk<
  void,
  {
    configurationId: string;
    condenserId: string;
    zoneId: string;
    spaceId: string;
  }
>(
  'configurations/removeSpaceFromZone',
  async (
    { configurationId, condenserId, zoneId, spaceId },
    { getState, dispatch }
  ) => {
    const state = getState() as RootState;
    const { configurations } = state.configurations;
    const config = findConfigurationById(configurations, configurationId);

    const newConfigurations = produce(configurations, (draft) => {
      const draftConfig = findConfigurationById(draft, configurationId);
      if (!draftConfig) return;

      const draftCondenser = findCondenserById(draftConfig, condenserId);
      if (!draftCondenser) return;

      const draftZone = findZoneById(draftCondenser, zoneId);
      if (!draftZone) return;

      const itemIndex = draftZone.zoneItems.findIndex(
        (item) => item.structureItemId === spaceId
      );

      if (itemIndex !== -1) {
        draftZone.zoneItems.splice(itemIndex, 1);
        draftZone.modified = true;
        draftCondenser.modified = true;
      }
    });

    if (config && isPtacPathwaySettings(state.project.projectSettings)) {
      dispatch(
        setConfigurations(
          rederivePtacConfigurationIn(
            newConfigurations,
            config,
            state.structure.structure
          )
        )
      );
      return;
    }

    const finalConfigurations = newConfigurations.map((c) => {
      if (c.uniqueId === configurationId) {
        return markCondensersStale(c, [condenserId]);
      }
      return c;
    });

    dispatch(setConfigurations(finalConfigurations));
  }
);

export const deleteZone = createAsyncThunk<
  void,
  {
    configurationId: string;
    condenserId: string;
    zoneId: string;
  }
>(
  'configurations/deleteZone',
  async ({ configurationId, condenserId, zoneId }, { getState, dispatch }) => {
    const state = getState() as RootState;
    const { configurations } = state.configurations;

    const newConfigurations = produce(configurations, (draft) => {
      const draftConfig = findConfigurationById(draft, configurationId);
      if (!draftConfig) return;

      if (isPtacPathwaySettings(state.project.projectSettings)) {
        draftConfig.condensers = draftConfig.condensers.filter(
          (condenser) => condenser.uniqueId !== condenserId
        );
        return;
      }

      const draftCondenser = findCondenserById(draftConfig, condenserId);
      if (!draftCondenser) return;

      draftCondenser.zones = draftCondenser.zones.filter(
        (zone) => zone.uniqueId !== zoneId
      );
      draftCondenser.modified = true;
    });

    const finalConfigurations = newConfigurations.map((c) => {
      if (c.uniqueId === configurationId) {
        return renumberZones(markCondensersStale(c, [condenserId]));
      }
      return c;
    });

    dispatch(setConfigurations(finalConfigurations));
  }
);

export const deleteEmptyZones = createAsyncThunk<
  void,
  { configurationId: string }
>(
  'configurations/deleteEmptyZones',
  async ({ configurationId }, { getState, dispatch }) => {
    const state = getState() as RootState;
    const { configurations } = state.configurations;

    const config = findConfigurationById(configurations, configurationId);
    if (!config) return;

    const hasEmptyZones = config.condensers.some((condenser) =>
      condenser.zones.some((zone) => zone.zoneItems.length === 0)
    );

    if (!hasEmptyZones) return;

    const newConfigurations = produce(configurations, (draft) => {
      const draftConfig = findConfigurationById(draft, configurationId);
      if (!draftConfig) return;

      if (isPtacPathwaySettings(state.project.projectSettings)) {
        draftConfig.condensers = draftConfig.condensers.filter((condenser) =>
          condenser.zones.some((zone) => zone.zoneItems.length > 0)
        );
        return;
      }

      draftConfig.condensers.forEach((condenser) => {
        // eslint-disable-next-line no-param-reassign
        condenser.zones = condenser.zones.filter(
          (zone) => zone.zoneItems.length > 0
        );
      });
    });

    const finalConfigurations = newConfigurations.map((c) => {
      if (c.uniqueId === configurationId) {
        return renumberZones(c);
      }
      return c;
    });

    dispatch(setConfigurations(finalConfigurations));
  }
);

export const resetConfigurationEquipment = createAsyncThunk<
  void,
  { configurationId: string }
>(
  'configurations/resetConfigurationEquipment',
  async ({ configurationId }, { getState, dispatch }) => {
    const state = getState() as RootState;
    const { configurations } = state.configurations;

    const config = findConfigurationById(configurations, configurationId);
    if (!config) return;

    const newConfigurations = produce(configurations, (draft) => {
      const draftConfig = findConfigurationById(draft, configurationId);
      if (!draftConfig) return;

      draftConfig.condensers.forEach((condenser) => {
        condenser.zones.forEach((zone) => {
          // eslint-disable-next-line no-param-reassign
          zone.airHandler = undefined;
          // eslint-disable-next-line no-param-reassign
          zone.selectedProduct = undefined;
        });
      });

      draftConfig.lineSets = [];
    });

    const finalConfigurations = newConfigurations.map((c) => {
      if (c.uniqueId === configurationId) {
        return markCondensersStale(renumberZones(c));
      }
      return c;
    });

    dispatch(setConfigurations(finalConfigurations));
  }
);

export const autoGenerateLinesets = createAsyncThunk<
  void,
  { configurationId: string }
>(
  'configurations/autoGenerateLinesets',
  async ({ configurationId }, { getState, dispatch }) => {
    const state = getState() as RootState;
    const { configurations } = state.configurations;
    const { structure } = state.structure;

    const config = findConfigurationById(configurations, configurationId);
    if (!config) return;

    const levels = structure;

    const getGridSpacesForLevelByIndex = (levelIndex: number) =>
      getGridSpacesForLevel(levels, levelIndex);

    const concealedDuctLayouts = buildConcealedDuctLayoutsForConfiguration(
      config,
      structure,
      getGridSpacesForLevelByIndex,
      state.ui.zoneDuctModes
    );

    const condensers = getVisibleCondensers(config);
    const airHandlers = getAirHandlersFromConfig(
      config,
      structure,
      concealedDuctLayouts
    );
    if (condensers.length === 0 || airHandlers.length === 0) return;

    const pairs = computeCondenserAirHandlerAssignment(
      config,
      structure,
      concealedDuctLayouts
    );

    const newLineSets = pairs
      .map((pair) => {
        const condenser = condensers.find(
          (c) => c.uniqueId === pair.condenserUniqueId
        );
        const airHandler = airHandlers.find(
          (a) => a.uniqueId === pair.airHandlerUniqueId
        );
        if (!condenser || !airHandler) return null;
        return buildLinesetForPair(
          condenser,
          airHandler,
          structure,
          getGridSpacesForLevelByIndex,
          configurationId
        );
      })
      .filter((ls): ls is NonNullable<typeof ls> => ls !== null);

    dispatch(
      setConfigurations(
        withConfigurationLinesets(configurations, configurationId, newLineSets)
      )
    );
  }
);

export const clearConfigurationLinesets = createAsyncThunk<
  void,
  { configurationId: string }
>(
  'configurations/clearConfigurationLinesets',
  async ({ configurationId }, { getState, dispatch }) => {
    const state = getState() as RootState;
    const { configurations } = state.configurations;

    const config = findConfigurationById(configurations, configurationId);
    if (!config?.lineSets?.length) return;

    dispatch(
      setConfigurations(
        withConfigurationLinesets(configurations, configurationId, [])
      )
    );
  }
);

// Keeps the hybrid whole-house configuration (utils/hybrid-whole-house-
// configuration) in step with Layout & Sizing while the
// 'hybrid-whole-house' System Design bypass is on: creates it when missing,
// rebuilds it in place (same ids) when the rooms or equipment positions it
// was built from changed, and leaves it alone otherwise. Idempotent — the
// watcher re-dispatches on every relevant change and after the reset
// watcher's configuration wipes.
export const syncHybridWholeHouseConfiguration = createAsyncThunk<
  ConfigurationType | null,
  void
>(
  'configurations/syncHybridWholeHouseConfiguration',
  async (_, { getState, dispatch }) => {
    const state = getState() as RootState;
    const { configurations } = state.configurations;
    const { structure, existingSystems, condenserLocations } = state.structure;

    const current = findHybridWholeHouseConfiguration(configurations);
    const desired = buildHybridWholeHouseConfiguration({
      current,
      structure,
      existingSystems,
      condenserLocations
    });
    if (!desired) return current;
    if (
      current &&
      hybridWholeHouseSignature(current) === hybridWholeHouseSignature(desired)
    ) {
      return current;
    }

    const nextConfigurations = current
      ? configurations.map((config) =>
          config.uniqueId === current.uniqueId ? desired : config
        )
      : [...configurations, desired];
    dispatch(setConfigurations(nextConfigurations));
    if (!current) {
      dispatch(addLocallyCreatedConfigurationId(desired.uniqueId));
      if (configurations.length === 0) {
        const createdIndex = nextConfigurations.findIndex(
          (config) => config.uniqueId === desired.uniqueId
        );
        if (createdIndex >= 0) {
          dispatch(setActiveConfig(createdIndex));
        }
      }
    }
    // A ducted zone needs no placed air handler to be complete, but the line
    // set from the condenser to the indoor unit drives the Add-Ons line-set
    // selection — the same auto-routing System Design offers.
    await dispatch(autoGenerateLinesets({ configurationId: desired.uniqueId }));
    return desired;
  }
);

export const seedDuctedDesignConfiguration = createAsyncThunk<
  ConfigurationType | null,
  void
>(
  'configurations/seedDuctedDesignConfiguration',
  async (_, { getState, dispatch }) => {
    const state = getState() as RootState;
    const { configurations } = state.configurations;
    const existing = findDuctedDesignConfiguration(configurations);
    if (existing) return existing;

    const {
      structure,
      existingSystems,
      condenserLocations,
      recommendationSelectionsBySystem,
      wantCompositionPicksBySystem
    } = state.structure;
    const seeded = buildDuctedDesignConfiguration({
      name: getNextDuctedDesignConfigurationName(configurations),
      structure,
      existingSystems,
      eligibleSystems: getChooseSystemsEligibleDuctedSystems(
        existingSystems,
        recommendationSelectionsBySystem,
        wantCompositionPicksBySystem
      ),
      condenserLocations
    });
    if (!seeded) return null;

    const nextConfigurations = [...configurations, seeded];
    dispatch(setConfigurations(nextConfigurations));
    dispatch(addLocallyCreatedConfigurationId(seeded.uniqueId));
    dispatch(setActiveConfig(nextConfigurations.length - 1));
    await dispatch(autoGenerateLinesets({ configurationId: seeded.uniqueId }));
    return seeded;
  }
);

export const syncDuctedNewSystemSlots = createAsyncThunk<
  DuctedNewSystemSlotsPlan | null,
  void
>(
  'configurations/syncDuctedNewSystemSlots',
  async (_, { getState, dispatch }) => {
    const state = getState() as RootState;
    const design = selectActiveDuctedDesign(state);
    if (!design) return null;

    const plan = planDuctedNewSystemSlots({
      design,
      existingSystems: state.structure.existingSystems,
      newSlotId: () => nanoid(5)
    });
    plan.removeIds.forEach((uniqueId) => {
      dispatch(removeExistingSystem({ uniqueId }));
    });
    plan.addIds.forEach((uniqueId) => {
      dispatch(addExistingSystem({ uniqueId }));
    });

    const synced = getState() as RootState;
    const { configurations } = synced.configurations;
    const existingSystemIds = new Set(
      synced.structure.existingSystems.map((system) => system.uniqueId)
    );
    const linked = withDuctedPairLinks(
      withoutOrphanedDuctedPairLinks(configurations, existingSystemIds),
      design.uniqueId,
      plan.links
    );
    if (linked !== configurations) {
      dispatch(setConfigurations(linked));
    }
    return plan;
  }
);

export const syncPtacConfigurations = createAsyncThunk<
  ConfigurationType[],
  void
>(
  'configurations/syncPtacConfigurations',
  async (_, { getState, dispatch }) => {
    const state = getState() as RootState;
    const { configurations } = state.configurations;
    const { structure } = state.structure;

    if (configurations.length === 0) {
      const created = newCustomPtacConfiguration(
        getNextCustomConfigurationName(configurations)
      );
      dispatch(setConfigurations([created]));
      dispatch(addLocallyCreatedConfigurationId(created.uniqueId));
      dispatch(setActiveConfig(0));
      return [created];
    }

    const homeOrientation = structureOrientationSetting(
      state.project.projectSettings
    );
    const refreshed = configurations.map((configuration) => {
      const desired = refreshPtacConfiguration({
        current: configuration,
        structure,
        homeOrientation
      });
      return ptacConfigurationSignature(configuration) ===
        ptacConfigurationSignature(desired)
        ? configuration
        : desired;
    });
    const unchanged = refreshed.every(
      (configuration, index) => configuration === configurations[index]
    );
    if (unchanged) return configurations;

    dispatch(setConfigurations(refreshed));
    return refreshed;
  }
);

export const generatePtacConfiguration = createAsyncThunk<
  ConfigurationType | null,
  void
>(
  'configurations/generatePtacConfiguration',
  async (_, { getState, dispatch }) => {
    const state = getState() as RootState;
    const { configurations } = state.configurations;
    const { structure } = state.structure;
    const generated = autoGeneratePtacConfiguration({
      current: newPtacConfiguration(
        nextPtacGeneratedConfigurationName(configurations),
        true
      ),
      structure,
      homeOrientation: structureOrientationSetting(
        state.project.projectSettings
      )
    });
    if (!generated) return null;

    dispatch(setConfigurations([...configurations, generated]));
    dispatch(addLocallyCreatedConfigurationId(generated.uniqueId));
    dispatch(setActiveConfig(configurations.length));
    return generated;
  }
);
